import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import AdminOverview from "../components/admin/AdminOverview";
import InviteDialog from "../components/admin/InviteDialog";
import EnhancedUserDirectory from "../components/admin/EnhancedUserDirectory";
import DashboardHeader from "../components/DashboardHeader";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const adminApiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const requestConfig = { withCredentials: true };

export default function AdminPage({ user, onLogout }) {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletePhrase, setDeletePhrase] = useState("");
  const [detailUser, setDetailUser] = useState(null);
  const [transferUser, setTransferUser] = useState(null);

  const loadPanel = useCallback(async () => {
    const params = {
      search,
      role: roleFilter,
      account_status: statusFilter,
      provider: providerFilter,
      sort,
    };
    const [overviewResponse, usersResponse, activityResponse] =
      await Promise.all([
        axios.get(`${adminApiUrl}/overview`, requestConfig),
        axios.get(`${adminApiUrl}/users`, { ...requestConfig, params }),
        axios.get(`${adminApiUrl}/activity`, requestConfig),
      ]);
    setOverview(overviewResponse.data);
    setUsers(usersResponse.data.users);
    setActivity(activityResponse.data.events);
  }, [providerFilter, roleFilter, search, sort, statusFilter]);

  useEffect(() => {
    loadPanel().catch(() =>
      toast.error("Cardfolio couldn’t load the admin workspace."),
    );
  }, [loadPanel]);

  const invite = async (payload) => {
    await axios.post(`${adminApiUrl}/invites`, payload, requestConfig);
    toast.success("Invitation sent.");
    loadPanel();
  };

  const changeRole = async (target, role) => {
    await axios.patch(
      `${adminApiUrl}/users/${target.user_id}/role`,
      { role },
      requestConfig,
    );
    toast.success(`${target.name} is now a ${role}.`);
    loadPanel();
  };

  const toggleStatus = async (target) => {
    const account_status =
      target.account_status === "active" ? "suspended" : "active";
    await axios.patch(
      `${adminApiUrl}/users/${target.user_id}/status`,
      { account_status },
      requestConfig,
    );
    toast.success(`${target.name} is now ${account_status}.`);
    loadPanel();
  };

  const deleteUser = async () => {
    await axios.delete(
      `${adminApiUrl}/users/${deletingUser.user_id}`,
      requestConfig,
    );
    toast.success(`${deletingUser.name} was permanently deleted.`);
    setDeletingUser(null);
    setDeletePhrase("");
    loadPanel();
  };

  const viewUser = async (target) => {
    const response = await axios.get(
      `${adminApiUrl}/users/${target.user_id}`,
      requestConfig,
    );
    setDetailUser(response.data);
  };

  const sendPasswordReset = async (target) => {
    await axios.post(
      `${adminApiUrl}/users/${target.user_id}/password-reset`,
      {},
      requestConfig,
    );
    toast.success(`Password reset email sent to ${target.name}.`);
    loadPanel();
  };

  const transferOwnership = async () => {
    await axios.post(
      `${adminApiUrl}/ownership/transfer`,
      { target_user_id: transferUser.user_id },
      requestConfig,
    );
    toast.success(`${transferUser.name} is now the Cardfolio Owner.`);
    setTransferUser(null);
    window.location.assign("/dashboard");
  };

  if (!overview) {
    return (
      <main className="loading-screen" data-testid="admin-loading-state">
        Loading administration
      </main>
    );
  }

  return (
    <div className="dashboard-shell" data-testid="admin-panel">
      <DashboardHeader user={user} onLogout={onLogout} activePage="Admin" />
      <main className="admin-main">
        <header className="admin-page-header">
          <div>
            <p className="eyebrow">Administration</p>
            <h1 data-testid="admin-page-heading">
              Keep the business in good hands.
            </h1>
            <p data-testid="admin-page-subtitle">
              Manage access, roles, and account health from one calm place.
            </p>
          </div>
          <div className="inventory-header-actions">
            <Link
              className="admin-secondary-button admin-catalog-shortcut"
              to="/admin/catalog"
              data-testid="catalog-management-link"
            >
              <span>Catalog workspace</span>
              <small>Build and publish reference records</small>
            </Link>
            <Link
              className="admin-secondary-button admin-catalog-shortcut"
              to="/admin/import-center"
              data-testid="catalog-import-center-link"
            >
              <span>Catalog Import Center</span>
              <small>Store and analyze a dataset safely</small>
            </Link>
            <button
              className="admin-primary-button"
              onClick={() => setInviteOpen(true)}
              type="button"
              data-testid="admin-invite-button"
            >
              <Plus size={17} strokeWidth={2} /> Invite user
            </button>
          </div>
        </header>
        <AdminOverview overview={overview} />
        <EnhancedUserDirectory
          users={users}
          actor={user}
          search={search}
          onSearch={setSearch}
          roleFilter={roleFilter}
          onRoleFilter={setRoleFilter}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          providerFilter={providerFilter}
          onProviderFilter={setProviderFilter}
          sort={sort}
          onSort={setSort}
          onRoleChange={changeRole}
          onStatusChange={toggleStatus}
          onDelete={setDeletingUser}
          onView={viewUser}
          onPasswordReset={sendPasswordReset}
          onTransferOwnership={setTransferUser}
        />
        <section className="admin-activity" data-testid="admin-activity-panel">
          <div className="admin-activity-heading">
            <div>
              <p className="eyebrow">Account history</p>
              <h2>Recent administration</h2>
            </div>
            <ShieldCheck size={21} strokeWidth={1.7} />
          </div>
          <div className="admin-activity-list">
            {activity.length ? (
              activity.map((event) => (
                <div
                  className="admin-activity-row"
                  data-testid={`admin-activity-${event.event_id}`}
                  key={event.event_id}
                >
                  <p>
                    <strong>{event.actor_name}</strong> {event.action}{" "}
                    <strong>{event.target_name}</strong>
                  </p>
                  <span>
                    {event.detail} ·{" "}
                    {new Date(event.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))
            ) : (
              <p
                className="admin-empty-activity"
                data-testid="admin-empty-activity"
              >
                Changes to accounts will appear here.
              </p>
            )}
          </div>
        </section>
      </main>
      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvite={invite}
        user={user}
      />
      <Dialog
        open={Boolean(deletingUser)}
        onOpenChange={() => setDeletingUser(null)}
      >
        <DialogContent
          className="admin-dialog"
          data-testid="delete-user-dialog"
        >
          <DialogHeader>
            <DialogTitle className="admin-dialog-title">
              Delete this account?
            </DialogTitle>
            <DialogDescription>
              {deletingUser?.name} will lose access permanently. Type DELETE to
              continue.
            </DialogDescription>
          </DialogHeader>
          <input
            className="admin-confirm-input"
            value={deletePhrase}
            onChange={(event) => setDeletePhrase(event.target.value)}
            placeholder="Type DELETE"
            data-testid="delete-user-confirmation-input"
          />
          <div className="admin-dialog-actions">
            <button
              className="admin-secondary-button"
              onClick={() => setDeletingUser(null)}
              type="button"
              data-testid="delete-user-cancel-button"
            >
              Cancel
            </button>
            <button
              className="admin-danger-button"
              onClick={deleteUser}
              type="button"
              disabled={deletePhrase !== "DELETE"}
              data-testid="delete-user-confirm-button"
            >
              Delete permanently
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(detailUser)}
        onOpenChange={() => setDetailUser(null)}
      >
        <DialogContent
          className="admin-dialog"
          data-testid="user-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="admin-dialog-title">
              {detailUser?.name}
            </DialogTitle>
            <DialogDescription>{detailUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="admin-detail-grid" data-testid="user-detail-grid">
            <span>
              Role <strong>{detailUser?.role}</strong>
            </span>
            <span>
              Status <strong>{detailUser?.account_status}</strong>
            </span>
            <span>
              Provider{" "}
              <strong>
                {detailUser?.auth_provider === "google" ? "Google" : "Email"}
              </strong>
            </span>
            <span>
              Created{" "}
              <strong>
                {detailUser?.created_at
                  ? new Date(detailUser.created_at).toLocaleDateString()
                  : "—"}
              </strong>
            </span>
            <span>
              Last login{" "}
              <strong>
                {detailUser?.last_login_at
                  ? new Date(detailUser.last_login_at).toLocaleDateString()
                  : "—"}
              </strong>
            </span>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(transferUser)}
        onOpenChange={() => setTransferUser(null)}
      >
        <DialogContent
          className="admin-dialog"
          data-testid="ownership-transfer-dialog"
        >
          <DialogHeader>
            <DialogTitle className="admin-dialog-title">
              Transfer ownership?
            </DialogTitle>
            <DialogDescription>
              {transferUser?.name} will become the Owner. You will become an
              Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="admin-dialog-actions">
            <button
              className="admin-secondary-button"
              onClick={() => setTransferUser(null)}
              type="button"
              data-testid="ownership-transfer-cancel-button"
            >
              Cancel
            </button>
            <button
              className="admin-danger-button"
              onClick={transferOwnership}
              type="button"
              data-testid="ownership-transfer-confirm-button"
            >
              Transfer ownership
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
